import ContainerWrapper from "@components/BasicComponents/Wrappers/ContainerWrapper/ContainerWrapper"
import Link from "@components/Theme/Link/Link"
import {getPosts} from "@config/modules"
import {translate} from "@helpers/translate"
import {MenuModuleType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import debounce from "@hooks/debounce"
import MenuDataJson from "@jsons/headerNavigation.json"
import {useRouter} from "next/router"
import React, {useEffect, useState} from "react"

import Typography, {Size, Variant} from "../Typography/Typography"
import checkIfLinkIsCurrent from "../utils/checkIfLinkIsCurrent"

import styles from "./Header.module.scss"
import HeaderMobile from "./HeaderMobile/HeaderMobile"
import LanguageSwitcher from "./LanguageSwitcher/LanguageSwitcher"
import LoginSelect from "./LoginSelect/LoginSelect"
import Navigation from "./Navigation/Navigation"
import Subheader from "./SubHeader/Subheader"

const Header = () => {
	const router = useRouter()

	const [activeLink, setActiveLink] = useState(-1)
	const [isShown, setIsShown] = useState<number>(-1)
	const [isScrolled, setIsScrolled] = useState(false)
	const [showHeader, setShowHeader] = useState(true)
	const [prevScrollPos, setPrevScrollPos] = useState(0)

	const menu = getPosts("menuModule", {data: MenuDataJson})
	const mainMenuWrapper = menu.find((element) => element.slug === "main-menu")

	const mainMenu: MenuModuleType[] = menu
		.filter((element) => element.parent?.id === mainMenuWrapper?.id)
		.map((element) => {
			element.parent = null
			return element
		})

	useEffect(() => {
		const handleScroll = debounce(() => {
			const currentScrollPos = window.pageYOffset
			const scrollDifference = prevScrollPos - currentScrollPos
			const visible = Math.abs(scrollDifference) >= 0

			if (visible && currentScrollPos > 300) {
				setShowHeader(currentScrollPos < prevScrollPos)
				setPrevScrollPos(currentScrollPos)
			}
			setIsScrolled(currentScrollPos > 100)

			if (currentScrollPos <= 0) {
				setShowHeader(true)
				setPrevScrollPos(currentScrollPos)
			}
		}, 0)

		window.addEventListener("scroll", handleScroll)
		return () => {
			window.removeEventListener("scroll", handleScroll)
			clearTimeout(handleScroll as unknown as NodeJS.Timeout)
		}
	}, [prevScrollPos])

	useEffect(() => {
		let active = -1
		mainMenu.forEach((item, index) => {
			item.children?.forEach((children) => {
				const {current, parent} = checkIfLinkIsCurrent(
					router.asPath,
					children?.link,
					children?.subLinksSelect,
				)
				if (current || parent) {
					active = index
				}
			})
		})
		setActiveLink(active)
	}, [router])

	const menuItem = mainMenu?.[activeLink]

	return (
		<header
			className={styles.mainHeaderWrapper}
			onMouseLeave={() => {
				if (isShown >= 0) {
					setIsShown(-1)
				}
			}}
		>
			<div
				className={`${styles.header} ${
					showHeader ? styles.show : styles.hide
				} ${isScrolled ? styles.scrolled : ""}`}
			>
				<ContainerWrapper style={{height: "100%"}}>
					<div className={styles.headerContainer}>
						<div className={styles.navBox}>
							<div className={styles.logoWrapper}>
								<Link
									className={styles.logoBox}
									link={"/"}
									withoutTarget
								>
									<img
										alt={"One Communications"}
										className={styles.logoHorizontal}
										src={
											"/logo-one-communications/OneCommunications-horizontal.svg"
										}
									/>
									<img
										alt={"One Communications"}
										className={styles.logoVertical}
										src={
											"/logo-one-communications/OneCommunications-vertical.svg"
										}
									/>
								</Link>
								<Typography
									fontWeight={200}
									size={Size.XS}
									className={styles.companyDescription}
									style={{textTransform: "uppercase"}}
									variant={Variant.Description}
									noPadding
								>
									{translate(
										"Communication Software Company",
										router.locale,
									)}
								</Typography>
							</div>
						</div>

						<div className={styles.headerButtons}>
							<Navigation
								isScrolled={isScrolled}
								isShown={isShown}
								setIsShown={setIsShown}
								menu={mainMenu}
							/>
							<div />
							<LanguageSwitcher
								setIsShown={setIsShown}
								isShown={isShown}
							/>
							<div />
							<LoginSelect
								setIsShown={setIsShown}
								isShown={isShown}
							/>
						</div>
					</div>
				</ContainerWrapper>
			</div>
			{Array.isArray(menuItem?.children) &&
				menuItem.children.length > 0 && (
					<Subheader
						hovered={false}
						data={menuItem}
						isScrolled={isScrolled}
					/>
				)}

			<HeaderMobile
				data={[...mainMenu]}
				showHeader={showHeader}
				hovered={false}
				isScrolled={isScrolled}
				subMenu={undefined}
				setIsShown={setIsShown}
				isShown={isShown}
				theme={"studio"}
			/>
		</header>
	)
}

export default Header
